import React, { useEffect, useState, useCallback } from 'react';
import API from '../services/api';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { Field, Row, StatusBadge, Notice, inputStyle, selectStyle, tabStyle, apiErrors } from '../components/insurance/formBits';
import { formatMoney, enrollmentBadge } from '../insurance/format';

// Insurance Billing Settings: the practice's billing identity (what goes on every
// claim), the fee schedule (CPT → charge), and each therapist's rendering-provider
// fields. Backend: /api/billing/settings, /fee-schedule, /therapists.

const SETTINGS_FIELDS = [
  ['legalName', 'Legal name (must match W-9)', 'legal_name'], ['npi', 'Billing NPI (Type 2)', 'npi'], ['taxonomyCode', 'Taxonomy code', 'taxonomy_code'],
  ['ein', 'EIN', 'ein'], ['addressLine1', 'Address', 'address_line1'], ['addressLine2', 'Address line 2', 'address_line2'],
  ['city', 'City', 'city'], ['state', 'State', 'state'], ['zip', 'ZIP (5 or 9 digits)', 'zip'], ['phone', 'Phone', 'phone'], ['contactName', 'Billing contact', 'contact_name'],
];

function settingsToForm(row) {
  const f = {};
  for (const [key, , col] of SETTINGS_FIELDS) f[key] = row && row[col] ? row[col] : '';
  f.autoCreateDraftClaims = Boolean(row && row.auto_create_draft_claims);
  f.therapistsSeeClaimAmounts = Boolean(row && row.therapists_see_claim_amounts);
  return f;
}

export default function AdminBillingSettingsPage() {
  const [tab, setTab] = useState('practice');
  const [status, setStatus] = useState(null);
  const [settings, setSettings] = useState(settingsToForm(null));
  const [fees, setFees] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [payers, setPayers] = useState([]);
  const [loadError, setLoadError] = useState('');

  const load = useCallback(async () => {
    try {
      const [s, st, f, t, p] = await Promise.all([
        API.get('/billing/settings'), API.get('/billing/status'), API.get('/billing/fee-schedule'), API.get('/billing/therapists'), API.get('/billing/payers'),
      ]);
      setSettings(settingsToForm(s.data)); setStatus(st.data); setFees(f.data); setTherapists(t.data); setPayers(p.data); setLoadError('');
    } catch (e) {
      setLoadError(apiErrors(e).message);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 16px' }}>
      <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--primary-color)' }}>Insurance Billing Settings</h1>
      <p style={{ margin: '4px 0 16px', fontSize: 13, color: 'var(--text-muted)' }}>
        What goes on every claim, what each service is billed at, and which therapists can bill insurance.
      </p>
      {status && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          <StatusBadge tone={status.stedi.configured ? 'good' : 'bad'}>Stedi: {status.stedi.configured ? `connected (${status.stedi.env} mode)` : 'not configured'}</StatusBadge>
          <StatusBadge tone={status.encryption.configured ? 'good' : 'bad'}>Encryption: {status.encryption.configured ? 'configured' : 'missing key'}</StatusBadge>
        </div>
      )}
      {loadError && <Notice tone="bad">{loadError}</Notice>}
      <div style={{ marginBottom: 12, borderBottom: '1px solid var(--border-color)' }}>
        <button style={tabStyle(tab === 'practice')} onClick={() => setTab('practice')}>Practice</button>
        <button style={tabStyle(tab === 'fees')} onClick={() => setTab('fees')}>Fee Schedule</button>
        <button style={tabStyle(tab === 'therapists')} onClick={() => setTab('therapists')}>Therapist Billing</button>
      </div>
      {tab === 'practice' && <PracticeTab form={settings} setForm={setSettings} onSaved={load} />}
      {tab === 'fees' && <FeesTab fees={fees} payers={payers} onChanged={load} />}
      {tab === 'therapists' && <TherapistsTab therapists={therapists} onChanged={load} />}
    </div>
  );
}

function PracticeTab({ form, setForm, onSaved }) {
  const [errors, setErrors] = useState({});
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  async function save(e) {
    e.preventDefault(); setSaving(true); setMsg(null);
    try {
      await API.put('/billing/settings', form);
      setErrors({}); setMsg({ tone: 'good', text: 'Saved.' }); onSaved();
    } catch (err) {
      const { fieldErrors, message } = apiErrors(err);
      setErrors(fieldErrors); setMsg({ tone: 'bad', text: message });
    } finally { setSaving(false); }
  }

  const field = ([k, label]) => (
    <Field key={k} label={label} error={errors[k]}><input style={inputStyle} value={form[k]} onChange={set(k)} aria-label={label} /></Field>
  );

  return (
    <Card>
      <form onSubmit={save}>
        <Row>{SETTINGS_FIELDS.slice(0, 3).map(field)}</Row>
        <Row>{SETTINGS_FIELDS.slice(3, 6).map(field)}</Row>
        <Row>{SETTINGS_FIELDS.slice(6).map(field)}</Row>
        <div style={{ marginTop: 14, display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 13 }}>
          <label><input type="checkbox" checked={form.autoCreateDraftClaims} onChange={set('autoCreateDraftClaims')} /> Automatically create draft claims for eligible sessions</label>
          <label><input type="checkbox" checked={form.therapistsSeeClaimAmounts} onChange={set('therapistsSeeClaimAmounts')} /> Therapists can see dollar amounts on their claims</label>
        </div>
        {msg && <Notice tone={msg.tone}>{msg.text}</Notice>}
        <div style={{ marginTop: 12 }}><Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save settings'}</Button></div>
      </form>
    </Card>
  );
}

const FEE_BLANK = { cptCode: '', description: '', charge: '', expectedAllowed: '', payerId: '', isActive: true };
function feeToForm(f) {
  return { cptCode: f.cpt_code, description: f.description, charge: f.charge ?? '', expectedAllowed: f.expected_allowed ?? '', payerId: f.payer_id || '', isActive: f.is_active };
}

function FeesTab({ fees, payers, onChanged }) {
  const [editingId, setEditingId] = useState(null);   // fee id, or 'new'
  const [draft, setDraft] = useState(FEE_BLANK);
  const [errors, setErrors] = useState({});
  const [msg, setMsg] = useState(null);
  const set = (k) => (e) => setDraft({ ...draft, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  function startEdit(f) { setEditingId(f.id); setDraft(feeToForm(f)); setErrors({}); setMsg(null); }
  function startNew() { setEditingId('new'); setDraft(FEE_BLANK); setErrors({}); setMsg(null); }
  function cancel() { setEditingId(null); setErrors({}); }

  async function save() {
    try {
      if (editingId === 'new') await API.post('/billing/fee-schedule', draft);
      else await API.put(`/billing/fee-schedule/${editingId}`, draft);
      setEditingId(null); setErrors({}); setMsg({ tone: 'good', text: 'Saved.' }); onChanged();
    } catch (err) {
      const { fieldErrors, message } = apiErrors(err);
      setErrors(fieldErrors); setMsg({ tone: 'bad', text: message });
    }
  }

  const editorRow = (key) => (
    <tr key={key} style={{ background: 'var(--surface-muted)' }}>
      <td style={td}><Field error={errors.cptCode} style={{ minWidth: 90 }}><input style={inputStyle} value={draft.cptCode} onChange={set('cptCode')} placeholder="90834" aria-label="CPT code" /></Field></td>
      <td style={td}><Field error={errors.description}><input style={inputStyle} value={draft.description} onChange={set('description')} aria-label="Description" /></Field></td>
      <td style={td}><Field error={errors.charge} style={{ minWidth: 90 }}><input style={inputStyle} value={draft.charge} onChange={set('charge')} placeholder="0.00" aria-label="Charge" /></Field></td>
      <td style={td}><Field error={errors.expectedAllowed} style={{ minWidth: 90 }}><input style={inputStyle} value={draft.expectedAllowed} onChange={set('expectedAllowed')} aria-label="Expected allowed" /></Field></td>
      <td style={td}>
        <Field error={errors.payerId} style={{ minWidth: 140 }}>
          <select style={selectStyle} value={draft.payerId} onChange={set('payerId')} aria-label="Payer">
            <option value="">All payers (default)</option>
            {payers.map((p) => <option key={p.id} value={p.id}>{p.display_name}</option>)}
          </select>
        </Field>
      </td>
      <td style={td}><label style={{ fontSize: 13 }}><input type="checkbox" checked={draft.isActive} onChange={set('isActive')} /> Active</label></td>
      <td style={{ ...td, whiteSpace: 'nowrap' }}>
        <Button variant="success" style={smallBtn} onClick={save}>Save</Button>{' '}
        <Button variant="ghost" style={smallBtn} onClick={cancel}>Cancel</Button>
      </td>
    </tr>
  );

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Claim lines take their charge from here. A payer-specific row wins over the default row for that code.</div>
        <Button onClick={startNew} disabled={editingId === 'new'}>Add code</Button>
      </div>
      {msg && <Notice tone={msg.tone}>{msg.text}</Notice>}
      <div style={{ overflowX: 'auto' }}>
        <table style={table}>
          <thead><tr style={{ background: 'var(--secondary-color)' }}>{['CPT', 'Description', 'Charge', 'Expected allowed', 'Payer', 'Active', ''].map((h, i) => <th key={i} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {editingId === 'new' && editorRow('new')}
            {fees.map((f) => (editingId === f.id ? editorRow(f.id) : (
              <tr key={f.id} style={{ borderBottom: '1px solid #F0EDE8', opacity: f.is_active ? 1 : 0.55 }}>
                <td style={{ ...td, fontFamily: 'monospace', fontWeight: 700 }}>{f.cpt_code}</td>
                <td style={td}>{f.description}</td>
                <td style={td}>{formatMoney(f.charge)}</td>
                <td style={td}>{formatMoney(f.expected_allowed)}</td>
                <td style={td}>{f.payer_name || <span style={{ color: 'var(--text-muted)' }}>All payers</span>}</td>
                <td style={td}>{f.is_active ? <StatusBadge tone="good">Active</StatusBadge> : <StatusBadge>Inactive</StatusBadge>}</td>
                <td style={td}><Button variant="ghost" style={smallBtn} onClick={() => startEdit(f)}>Edit</Button></td>
              </tr>
            )))}
            {fees.length === 0 && editingId !== 'new' && <tr><td style={td} colSpan={7}>No codes yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function therapistToForm(t) {
  return { npi: t.npi || '', taxonomyCode: t.taxonomy_code || '', licenseState: t.license_state || '', caqhId: t.caqh_id || '', licenseType: t.license_type || '', licenseNumber: t.license_number || '', insuranceEnabled: Boolean(t.insurance_enabled) };
}

function TherapistsTab({ therapists, onChanged }) {
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [errors, setErrors] = useState({});
  const [msg, setMsg] = useState(null);
  const set = (k) => (e) => setDraft({ ...draft, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  async function save() {
    try {
      await API.put(`/billing/therapists/${editingId}/billing-profile`, draft);
      setEditingId(null); setErrors({}); setMsg({ tone: 'good', text: 'Saved.' }); onChanged();
    } catch (err) {
      const { fieldErrors, message } = apiErrors(err);
      setErrors(fieldErrors); setMsg({ tone: 'bad', text: message });
    }
  }

  return (
    <Card>
      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>
        Only therapists with insurance enabled (valid NPI and taxonomy) can have claims created. Enrollment dates are managed on the Payers page.
      </div>
      {msg && <Notice tone={msg.tone}>{msg.text}</Notice>}
      <div style={{ overflowX: 'auto' }}>
        <table style={table}>
          <thead><tr style={{ background: 'var(--secondary-color)' }}>{['Therapist', 'License', 'NPI', 'Taxonomy', 'State', 'CAQH', 'Insurance', 'Enrollments', ''].map((h, i) => <th key={i} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {therapists.map((t) => (
              editingId === t.id ? (
                <tr key={t.id} style={{ background: 'var(--surface-muted)' }}>
                  <td style={td}><strong>{t.first_name} {t.last_name}</strong></td>
                  <td style={td}>
                    <Field error={errors.licenseType} style={{ minWidth: 100 }}><input style={inputStyle} value={draft.licenseType} onChange={set('licenseType')} placeholder="LPCC" aria-label="License type" /></Field>
                    <Field error={errors.licenseNumber} style={{ minWidth: 100 }}><input style={inputStyle} value={draft.licenseNumber} onChange={set('licenseNumber')} placeholder="License #" aria-label="License number" /></Field>
                  </td>
                  <td style={td}><Field error={errors.npi} style={{ minWidth: 130 }}><input style={inputStyle} value={draft.npi} onChange={set('npi')} placeholder="10 digits" aria-label="NPI" /></Field></td>
                  <td style={td}><Field error={errors.taxonomyCode} style={{ minWidth: 130 }}><input style={inputStyle} value={draft.taxonomyCode} onChange={set('taxonomyCode')} placeholder="101Y00000X" aria-label="Taxonomy code" /></Field></td>
                  <td style={td}><Field error={errors.licenseState} style={{ minWidth: 60 }}><input style={inputStyle} value={draft.licenseState} onChange={set('licenseState')} placeholder="KY" maxLength={2} aria-label="License state" /></Field></td>
                  <td style={td}><Field error={errors.caqhId} style={{ minWidth: 110 }}><input style={inputStyle} value={draft.caqhId} onChange={set('caqhId')} aria-label="CAQH ID" /></Field></td>
                  <td style={td}><Field error={errors.insuranceEnabled} style={{ minWidth: 120 }}><label style={{ fontSize: 13 }}><input type="checkbox" checked={draft.insuranceEnabled} onChange={set('insuranceEnabled')} aria-label="Insurance enabled" /> Enabled</label></Field></td>
                  <td style={td}></td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <Button variant="success" style={smallBtn} onClick={save}>Save</Button>{' '}
                    <Button variant="ghost" style={smallBtn} onClick={() => { setEditingId(null); setErrors({}); }}>Cancel</Button>
                  </td>
                </tr>
              ) : (
                <tr key={t.id} style={{ borderBottom: '1px solid #F0EDE8', opacity: t.is_active ? 1 : 0.55 }}>
                  <td style={td}><strong>{t.first_name} {t.last_name}</strong>{!t.is_active && <span style={{ color: 'var(--text-muted)' }}> (inactive)</span>}</td>
                  <td style={td}>{t.license_type || '—'}{t.license_number ? ` #${t.license_number}` : ''}</td>
                  <td style={{ ...td, fontFamily: 'monospace' }}>{t.npi || '—'}</td>
                  <td style={{ ...td, fontFamily: 'monospace' }}>{t.taxonomy_code || '—'}</td>
                  <td style={td}>{t.license_state || '—'}</td>
                  <td style={td}>{t.caqh_id || '—'}</td>
                  <td style={td}>{t.insurance_enabled ? <StatusBadge tone="good">Enabled</StatusBadge> : <StatusBadge>Off</StatusBadge>}</td>
                  <td style={td}>
                    {(t.enrollments || []).length === 0 ? <span style={{ color: 'var(--text-muted)' }}>None</span> : (t.enrollments || []).map((e) => {
                      const b = enrollmentBadge(e);
                      return <div key={e.id} style={{ marginBottom: 3 }}><StatusBadge tone={b.tone}>{e.payerName}: {b.label}</StatusBadge></div>;
                    })}
                  </td>
                  <td style={td}><Button variant="ghost" style={smallBtn} onClick={() => { setEditingId(t.id); setDraft(therapistToForm(t)); setErrors({}); setMsg(null); }}>Edit</Button></td>
                </tr>
              )
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

const table = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const th = { padding: '8px 10px', textAlign: 'left', color: 'var(--primary-color)', fontWeight: 600, whiteSpace: 'nowrap' };
const td = { padding: '8px 10px', verticalAlign: 'top' };
const smallBtn = { padding: '6px 10px', fontSize: 12 };
